import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Printer,
  Package,
  Truck,
  CheckCircle2,
  Loader2,
  AlertCircle,
  MapPin,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { ShopifyOrder } from '../../types/shopify';
import { formatCurrency, formatOrderDate } from '../../types/shopify';
import type { ProductBinLocation } from '../../types/warehouse';
import { getAdminId } from '../../utils/getAdminId';

interface OrderInvoiceProps {
  order: ShopifyOrder;
  onClose: () => void;
  onFulfilled?: () => void;
  autoFulfillmentEnabled: boolean;
  defaultCarrier: string;
}

interface CompanyInfo {
  company_name?: string;
  address?: string;
  kvk_number?: string;
  vat_number?: string;
  bank_account?: string;
  avatar_data?: string;
}

export const OrderInvoice: React.FC<OrderInvoiceProps> = ({
  order,
  onClose,
  onFulfilled,
  autoFulfillmentEnabled,
  defaultCarrier,
}) => {
  const { user } = useAuth();
  const printRef = useRef<HTMLDivElement>(null);
  const [trackingCode, setTrackingCode] = useState('');
  const [carrier, setCarrier] = useState(defaultCarrier);
  const [fulfilling, setFulfilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({});
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [binLocations, setBinLocations] = useState<Map<string, ProductBinLocation[]>>(new Map());
  const [imageUrlBySku, setImageUrlBySku] = useState<Map<string, string | null>>(new Map());
  const [variantBySku, setVariantBySku] = useState<Map<string, { color: string | null; size: string | null }>>(new Map());
  const [loadingBins, setLoadingBins] = useState(true);

  useEffect(() => {
    const loadCompanyInfo = async () => {
      if (!user) return;

      const adminId = getAdminId(user);

      const { data: userData } = await supabase
        .from('users')
        .select('company_name, address, kvk_number, vat_number, bank_account')
        .eq('id', adminId)
        .maybeSingle();

      const { data: settingsData } = await supabase
        .from('user_settings')
        .select('avatar_data')
        .eq('user_id', adminId)
        .maybeSingle();

      setCompanyInfo({
        ...userData,
        avatar_data: settingsData?.avatar_data,
      });
    };

    loadCompanyInfo();
  }, [user]);

  useEffect(() => {
    const loadInvoiceData = async () => {
      if (!user || !order.line_items || order.line_items.length === 0) {
        setBinLocations(new Map());
        setImageUrlBySku(new Map());
        setVariantBySku(new Map());
        setLoadingBins(false);
        return;
      }

      setLoadingBins(true);

      try {
        const adminId = getAdminId(user);

        const skus = order.line_items
          .map(item => item.sku)
          .filter((sku): sku is string => sku !== null && sku !== '');

        let products: {
          id: string;
          sku: string;
          image_url: string | null;
          color_id: string | null;
          size_id: string | null;
        }[] = [];
        if (skus.length > 0) {
          const { data: matchedProducts } = await supabase
            .from('products')
            .select('id, sku, image_url, color_id, size_id')
            .eq('admin_id', adminId)
            .in('sku', skus);
          products = matchedProducts || [];
        }

        const colorIds = Array.from(
          new Set(products.map(p => p.color_id).filter((v): v is string => !!v)),
        );
        const sizeIds = Array.from(
          new Set(products.map(p => p.size_id).filter((v): v is string => !!v)),
        );
        const [colorsRes, sizesRes] = await Promise.all([
          colorIds.length > 0
            ? supabase.from('product_colors').select('id, name').in('id', colorIds)
            : Promise.resolve({ data: [] as { id: string; name: string }[] }),
          sizeIds.length > 0
            ? supabase.from('product_sizes').select('id, name').in('id', sizeIds)
            : Promise.resolve({ data: [] as { id: string; name: string }[] }),
        ]);
        const colorById = new Map((colorsRes.data || []).map(c => [c.id, c.name]));
        const sizeById = new Map((sizesRes.data || []).map(s => [s.id, s.name]));

        const normalizedKey = (s: string) => s.toLowerCase().trim();
        const skuToProductId = new Map(products.map(p => [normalizedKey(p.sku!), p.id]));
        const imageMap = new Map<string, string | null>();
        const variantMap = new Map<string, { color: string | null; size: string | null }>();
        for (const p of products) {
          if (!p.sku) continue;
          const key = normalizedKey(p.sku);
          imageMap.set(key, p.image_url || null);
          variantMap.set(key, {
            color: p.color_id ? colorById.get(p.color_id) ?? null : null,
            size: p.size_id ? sizeById.get(p.size_id) ?? null : null,
          });
        }
        setImageUrlBySku(imageMap);
        setVariantBySku(variantMap);

        const productIds = products.map(p => p.id);
        let binData: unknown[] | null = null;
        if (productIds.length > 0) {
          const { data, error } = await supabase
            .rpc('get_product_bin_locations', {
              p_product_ids: productIds,
              p_admin_id: adminId,
            });
          if (!error) binData = data;
          else console.error('Error fetching bin locations:', error);
        }

        const locationMap = new Map<string, ProductBinLocation[]>();
        if (binData && Array.isArray(binData)) {
          for (const item of binData as { product_id: string; [k: string]: unknown }[]) {
            const location: ProductBinLocation = {
              bin_inventory_id: item.bin_inventory_id as string,
              quantity: item.quantity as number,
              warehouse_id: item.warehouse_id as string,
              warehouse_name: item.warehouse_name as string,
              zone_id: item.zone_id as string | null,
              zone_code: (item.zone_code as string) || '',
              rack_id: item.rack_id as string,
              rack_code: item.rack_code as string,
              bin_id: item.bin_id as string,
              bin_code: item.bin_code as string,
              full_address: item.full_address as string,
              short_code: item.short_code as string,
            };
            const existing = locationMap.get(item.product_id) || [];
            existing.push(location);
            locationMap.set(item.product_id, existing);
          }
        }

        const skuLocationMap = new Map<string, ProductBinLocation[]>();
        for (const [normSku, productId] of skuToProductId) {
          const locations = locationMap.get(productId);
          if (locations && locations.length > 0) {
            skuLocationMap.set(normSku, locations);
          }
        }
        setBinLocations(skuLocationMap);
      } catch (err) {
        console.error('Error loading invoice data:', err);
      } finally {
        setLoadingBins(false);
      }
    };

    loadInvoiceData();
  }, [user, order.line_items]);

  const handlePrint = () => {
    if (autoFulfillmentEnabled && !order.fulfillment_status) {
      setShowTrackingModal(true);
    } else {
      executePrint();
    }
  };

  const executePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Order ${order.order_number}</title>
            <style>
              * { box-sizing: border-box; margin: 0; padding: 0; }
              body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1a1a1a; }
              .invoice { max-width: 800px; margin: 0 auto; }
              .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
              .logo img { max-height: 80px; width: auto; }
              .order-info { text-align: right; }
              .order-number { font-size: 28px; font-weight: bold; color: #0891b2; }
              .order-date { color: #666; margin-top: 8px; }
              .addresses { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 40px; }
              .address-block h3 { font-size: 12px; text-transform: uppercase; color: #666; margin-bottom: 8px; letter-spacing: 1px; }
              .address-block p { line-height: 1.6; }
              .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
              .items-table th { background: #f8fafc; padding: 12px; text-align: left; font-size: 12px; text-transform: uppercase; color: #666; border-bottom: 2px solid #e2e8f0; }
              .items-table td { padding: 12px; border-bottom: 1px solid #e2e8f0; }
              .items-table .qty { text-align: center; }
              .items-table .price { text-align: right; }
              .totals { display: flex; justify-content: flex-end; margin-bottom: 40px; }
              .totals-table { width: 280px; }
              .totals-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e2e8f0; }
              .totals-row.total { font-weight: bold; font-size: 18px; border-top: 2px solid #1a1a1a; border-bottom: none; padding-top: 12px; }
              .tracking { background: #f0f9ff; border: 1px solid #bae6fd; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
              .tracking h4 { color: #0369a1; margin-bottom: 8px; }
              .footer { text-align: center; color: #666; font-size: 12px; padding-top: 20px; border-top: 1px solid #e2e8f0; }
              .items-table img { width: 50px; height: 50px; object-fit: contain; border-radius: 4px; border: 1px solid #e2e8f0; background: #f8fafc; }
              @media print {
                body { padding: 20px; }
                .no-print { display: none !important; }
                .items-table img { width: 50px; height: 50px; object-fit: contain; border-radius: 4px; background: #f8fafc; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const handleFulfillAndPrint = async () => {
    if (!trackingCode.trim()) {
      setError('Please enter a tracking code');
      return;
    }

    setFulfilling(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      const adminId = getAdminId(user);

      const { data: rpcResult, error: rpcError } = await supabase.rpc('update_shopify_order_printed', {
        p_order_id: order.id,
        p_admin_id: adminId,
        p_tracking_code: trackingCode,
        p_carrier: carrier,
        p_printed_by: user?.id,
      });

      if (rpcError) {
        console.error('RPC error updating order:', rpcError);
      } else {
        const result = rpcResult as { success: boolean; error?: string };
        if (!result.success) {
          console.error('Failed to update order:', result.error);
        }
      }

      setShowTrackingModal(false);
      executePrint();

      setSuccess('Invoice printed! Fulfilling order in Shopify...');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-fulfill-order`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderId: order.shopify_order_id,
            trackingCode: trackingCode,
            carrier: carrier,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.warn('Fulfillment API not yet deployed:', data.error);
        setSuccess('Invoice printed! Auto-fulfillment will be available after deployment.');
      } else {
        setSuccess('Order fulfilled successfully!');
        if (onFulfilled) {
          onFulfilled();
        }
      }

      setTimeout(() => {
        setSuccess(null);
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Fulfillment error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fulfill order');
    } finally {
      setFulfilling(false);
    }
  };

  const shippingAddress = order.shipping_address;
  const isUnfulfilled = !order.fulfillment_status || order.fulfillment_status === 'UNFULFILLED';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
      <div className="min-h-screen py-8 px-4 flex items-start justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Order Invoice - {order.order_number}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Print packing slip for this order
              </p>
            </div>
            <div className="flex items-center gap-3">
              {isUnfulfilled && autoFulfillmentEnabled && (
                <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium rounded-full flex items-center gap-1">
                  <Truck className="h-3 w-3" />
                  Auto-fulfill on print
                </span>
              )}
              <Button onClick={handlePrint} disabled={fulfilling}>
                <Printer className="h-4 w-4 mr-2" />
                Print Invoice
              </Button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          {success && (
            <div className="mx-6 mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
            </div>
          )}

          <div ref={printRef} className="p-8 bg-white">
            <div className="invoice">
              <div className="header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
                <div className="logo">
                  {companyInfo.avatar_data ? (
                    <img src={companyInfo.avatar_data} alt="Logo" style={{ maxHeight: '80px', width: 'auto' }} />
                  ) : (
                    <div style={{ height: '80px', width: '120px' }}></div>
                  )}
                </div>
                <div className="order-info" style={{ textAlign: 'right' }}>
                  <div className="order-number" style={{ fontSize: '28px', fontWeight: 'bold', color: '#0891b2' }}>
                    {order.order_number}
                  </div>
                  <div className="order-date" style={{ color: '#666', marginTop: '8px' }}>
                    {formatOrderDate(order.order_date)}
                  </div>
                </div>
              </div>

              <div className="addresses" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '40px' }}>
                <div className="address-block">
                  <h3 style={{ fontSize: '12px', textTransform: 'uppercase', color: '#666', marginBottom: '8px', letterSpacing: '1px' }}>
                    From
                  </h3>
                  <p style={{ lineHeight: '1.6' }}>
                    <strong>{companyInfo.company_name || user?.username}</strong><br />
                    {companyInfo.address && (
                      <span style={{ whiteSpace: 'pre-line' }}>{companyInfo.address}</span>
                    )}
                    {companyInfo.kvk_number && (
                      <><br />KVK: {companyInfo.kvk_number}</>
                    )}
                    {companyInfo.vat_number && (
                      <><br />BTW: {companyInfo.vat_number}</>
                    )}
                  </p>
                </div>
                <div className="address-block">
                  <h3 style={{ fontSize: '12px', textTransform: 'uppercase', color: '#666', marginBottom: '8px', letterSpacing: '1px' }}>
                    Ship To
                  </h3>
                  <p style={{ lineHeight: '1.6' }}>
                    {shippingAddress ? (
                      <>
                        <strong>{shippingAddress.name || `${order.customer_first_name} ${order.customer_last_name}`}</strong><br />
                        {shippingAddress.address1}<br />
                        {shippingAddress.address2 && <>{shippingAddress.address2}<br /></>}
                        {shippingAddress.zip} {shippingAddress.city}<br />
                        {shippingAddress.province && <>{shippingAddress.province}, </>}
                        {shippingAddress.country}
                        {shippingAddress.phone && <><br />Tel: {shippingAddress.phone}</>}
                      </>
                    ) : (
                      <>
                        <strong>{order.customer_first_name} {order.customer_last_name}</strong><br />
                        {order.customer_email}
                      </>
                    )}
                  </p>
                </div>
              </div>

              <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
                <thead>
                  <tr>
                    <th style={{ background: '#f8fafc', padding: '12px', textAlign: 'center', fontSize: '12px', textTransform: 'uppercase', color: '#666', borderBottom: '2px solid #e2e8f0', width: '70px' }}>
                      Image
                    </th>
                    <th style={{ background: '#f8fafc', padding: '12px', textAlign: 'left', fontSize: '12px', textTransform: 'uppercase', color: '#666', borderBottom: '2px solid #e2e8f0' }}>
                      Product
                    </th>
                    <th style={{ background: '#f8fafc', padding: '12px', textAlign: 'left', fontSize: '12px', textTransform: 'uppercase', color: '#666', borderBottom: '2px solid #e2e8f0' }}>
                      SKU
                    </th>
                    <th style={{ background: '#0891b2', padding: '12px', textAlign: 'center', fontSize: '12px', textTransform: 'uppercase', color: '#fff', borderBottom: '2px solid #e2e8f0', fontWeight: '700' }}>
                      Bin
                    </th>
                    <th style={{ background: '#f8fafc', padding: '12px', textAlign: 'center', fontSize: '12px', textTransform: 'uppercase', color: '#666', borderBottom: '2px solid #e2e8f0' }}>
                      Qty
                    </th>
                    <th style={{ background: '#f8fafc', padding: '12px', textAlign: 'right', fontSize: '12px', textTransform: 'uppercase', color: '#666', borderBottom: '2px solid #e2e8f0' }}>
                      Price
                    </th>
                    <th style={{ background: '#f8fafc', padding: '12px', textAlign: 'right', fontSize: '12px', textTransform: 'uppercase', color: '#666', borderBottom: '2px solid #e2e8f0' }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {order.line_items.map((item) => {
                    const normSku = item.sku?.toLowerCase().trim() ?? '';
                    const itemBinLocations = normSku ? (binLocations.get(normSku) || []) : [];
                    const imageUrl = normSku ? (imageUrlBySku.get(normSku) ?? null) : null;
                    const variant = normSku ? variantBySku.get(normSku) : undefined;
                    const colorName = variant?.color ?? null;
                    const sizeName = variant?.size ?? null;
                    const isMissing = !normSku || itemBinLocations.length === 0;
                    return (
                      <tr key={item.id}>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center', verticalAlign: 'middle' }}>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={item.title}
                              crossOrigin="anonymous"
                              style={{ width: 50, height: 50, objectFit: 'contain', borderRadius: 4, border: '1px solid #e2e8f0', background: '#f8fafc' }}
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div style={{ width: 50, height: 50, background: '#f1f5f9', borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 10 }}>
                              No Image
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                          <div>{item.title}</div>
                          {(colorName || sizeName) && (
                            <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {colorName && (
                                <span style={{ background: '#f3e8ff', color: '#6b21a8', border: '1px solid #d8b4fe', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                  Color: {colorName}
                                </span>
                              )}
                              {sizeName && (
                                <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                  Size: {sizeName}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#666' }}>
                          {item.sku || '-'}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center', fontWeight: 700, fontSize: '14px', verticalAlign: 'middle' }}>
                          {isMissing ? (
                            <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 8px', borderRadius: 4, display: 'inline-block', fontWeight: 600 }}>
                              We don&apos;t have it
                            </span>
                          ) : (
                            <span style={{ color: '#0891b2', background: '#f0f9ff', padding: '4px 8px', borderRadius: 4, display: 'inline-block' }}>
                              {itemBinLocations[0].short_code}
                              {itemBinLocations.length > 1 && (
                                <span style={{ marginLeft: 4, fontSize: 10, color: '#64748b' }}>
                                  +{itemBinLocations.length - 1}
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                          {item.quantity}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>
                          {formatCurrency(parseFloat(item.price), item.currency)}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 500 }}>
                          {formatCurrency(parseFloat(item.price) * item.quantity, item.currency)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="totals" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '40px' }}>
                <div className="totals-table" style={{ width: '280px' }}>
                  <div className="totals-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span>Subtotal</span>
                    <span>{formatCurrency(order.subtotal_price, order.currency)}</span>
                  </div>
                  <div className="totals-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span>Shipping</span>
                    <span>{formatCurrency(order.total_shipping, order.currency)}</span>
                  </div>
                  <div className="totals-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span>Tax</span>
                    <span>{formatCurrency(order.total_tax, order.currency)}</span>
                  </div>
                  <div className="totals-row total" style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontWeight: 'bold', fontSize: '18px', borderTop: '2px solid #1a1a1a' }}>
                    <span>Total</span>
                    <span>{formatCurrency(order.total_price, order.currency)}</span>
                  </div>
                </div>
              </div>

              {order.customer_note && (
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', padding: '16px', borderRadius: '8px', marginBottom: '30px' }}>
                  <h4 style={{ color: '#92400e', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Customer Note</h4>
                  <p style={{ color: '#78350f', fontSize: '14px' }}>{order.customer_note}</p>
                </div>
              )}

              <div className="footer" style={{ textAlign: 'center', color: '#666', fontSize: '12px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                <p>Thank you for your order!</p>
                <p style={{ marginTop: '4px' }}>Printed on {new Date().toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button onClick={handlePrint} disabled={fulfilling}>
              <Printer className="h-4 w-4 mr-2" />
              Print Invoice
            </Button>
          </div>
        </div>
      </div>

      {showTrackingModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowTrackingModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Package className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Enter Tracking Info
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Order will be fulfilled in Shopify after print
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tracking Code *
                </label>
                <input
                  type="text"
                  value={trackingCode}
                  onChange={(e) => setTrackingCode(e.target.value)}
                  placeholder="e.g., 3SXXX123456789"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Carrier
                </label>
                <select
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="PostNL">PostNL</option>
                  <option value="DHL">DHL</option>
                  <option value="DPD">DPD</option>
                  <option value="UPS">UPS</option>
                  <option value="FedEx">FedEx</option>
                  <option value="GLS">GLS</option>
                  <option value="Budbee">Budbee</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Auto-fulfillment enabled:</strong> After printing, this order will be marked as fulfilled in Shopify and the customer will receive a shipping confirmation email.
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => setShowTrackingModal(false)}
                className="flex-1"
                disabled={fulfilling}
              >
                Cancel
              </Button>
              <Button
                onClick={handleFulfillAndPrint}
                className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                disabled={fulfilling || !trackingCode.trim()}
              >
                {fulfilling ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Printer className="h-4 w-4 mr-2" />
                    Print & Fulfill
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderInvoice;
